import { useEffect, useRef, useState } from "react";

import { DefaultSidebar, Sidebar, THEME } from "@excalidraw/excalidraw";
import { DesignPanel } from "@excalidraw/excalidraw/components/DesignPanel";
import { LayersPanel } from "@excalidraw/excalidraw/components/LayersPanel";
import {
  layersIcon,
  nibIcon,
  playerPlayIcon,
  settingsIcon,
  sidebarRightIcon,
} from "@excalidraw/excalidraw/components/icons";
import { LinkButton } from "@excalidraw/excalidraw/components/LinkButton";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { useExcalidrawSetAppState } from "@excalidraw/excalidraw/components/App";

import { FlipbookProvider, useFlipbook } from "../flipbook/FlipbookContext";

import { ActiveClipProvider } from "./ActiveClipContext";
import { AnimationPanel } from "./AnimationPanel";
import { SettingsPanel } from "./SettingsPanel";
import { CanvasAnimationBar } from "./CanvasAnimationBar";
import { FlipbookBar } from "./FlipbookBar";
import { StudioMode } from "./studio/Studio";
import {
  SidebarResizeHandle,
  SIDEBAR_MIN_WIDTH,
  clampSidebarWidth,
} from "./SidebarResizeHandle";

import "./AppSidebar.scss";

const TAB_TITLES: Record<string, string> = {
  layers: "Layers",
  properties: "Design",
  animation: "Animation",
  settings: "Export",
};

/** name of the currently open tab, shown under the (icon-only) tab bar */
const ActiveTabTitle = () => {
  const { openSidebar } = useUIAppState();
  const title = openSidebar?.tab && TAB_TITLES[openSidebar.tab];

  return title ? <div className="app-sidebar-title">{title}</div> : null;
};

const PropertiesPanel = () => {
  return (
    <div className="properties-panel">
      <DesignPanel />
    </div>
  );
};

/** Drawing with a left-toolbar tool flips the panel to Design, so the new
 * element's properties are already on screen when the stroke ends.
 * ponytail: only switches an already-open panel — doesn't force it open.
 * Skipped entirely while flipbook is open/active: drawing there means
 * authoring pages, and flipping the tab would drop animation mode and
 * unmount the dock (and the mini preview window) mid-book. */
const DesignTabOnDraw = () => {
  const { newElement } = useUIAppState();
  const setAppState = useExcalidrawSetAppState();
  const { open, flipbookActive } = useFlipbook();
  const drawnElementId = newElement?.id;
  // read through a ref so closing flipbook later can't re-fire the effect
  // for an old stroke and yank the tab after the fact
  const inFlipbookRef = useRef(false);
  inFlipbookRef.current = open || flipbookActive;
  useEffect(() => {
    if (drawnElementId && !inFlipbookRef.current) {
      setAppState((state) =>
        state.openSidebar?.name === "default" &&
        state.openSidebar.tab !== "properties"
          ? { openSidebar: { ...state.openSidebar, tab: "properties" } }
          : null,
      );
    }
  }, [drawnElementId, setAppState]);
  return null;
};

export const AppSidebar = () => {
  const { theme } = useUIAppState();

  // session-only (not persisted across reloads) — matches how the rest of
  // the sidebar's UI state (open tab, dock preference) already behaves.
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_MIN_WIDTH);

  // re-clamp on window resize so the panel can't end up wider than the
  // visible browser window if it's narrowed after the sidebar was widened
  useEffect(() => {
    const onResize = () => {
      setSidebarWidth((width) => clampSidebarWidth(width));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <FlipbookProvider>
      <ActiveClipProvider>
        <DesignTabOnDraw />
        <CanvasAnimationBar />
        <FlipbookBar />
        <StudioMode />
        {/* the panel's open/close toggle. Deliberately NOT
            `DefaultSidebar.Trigger` (which tunnels into LayerUI's top-right
            cluster, a stacking context the panel always paints over) — this
            renders here, as a sibling of the panel, so it can straddle the
            panel's top-right corner. See `.app-sidebar-toggle` in the scss. */}
        <Sidebar.Trigger
          name="default"
          tab="properties"
          icon={sidebarRightIcon}
          title="Toggle panel"
          className="app-sidebar-toggle"
        />
        <DefaultSidebar
          className="app-properties-sidebar"
          style={
            {
              "--right-sidebar-width": `${sidebarWidth}px`,
            } as React.CSSProperties
          }
        >
          <SidebarResizeHandle
            width={sidebarWidth}
            onWidthChange={setSidebarWidth}
          />
          <DefaultSidebar.TabTriggers>
            <Sidebar.TabTrigger tab="layers" title="Layers">
              {layersIcon}
            </Sidebar.TabTrigger>
            <Sidebar.TabTrigger tab="properties" title="Design">
              {nibIcon}
            </Sidebar.TabTrigger>
            <Sidebar.TabTrigger tab="animation" title="Animation">
              {playerPlayIcon}
            </Sidebar.TabTrigger>
            <Sidebar.TabTrigger tab="settings" title="Screens & export">
              {settingsIcon}
            </Sidebar.TabTrigger>
          </DefaultSidebar.TabTriggers>
          <ActiveTabTitle />
          <Sidebar.Tab tab="layers">
            <LayersPanel />
          </Sidebar.Tab>
          <Sidebar.Tab tab="properties">
            <PropertiesPanel />
          </Sidebar.Tab>
          <Sidebar.Tab tab="presentation" className="px-3">
            <div className="app-sidebar-promo-container">
              <div
                className="app-sidebar-promo-image"
                style={{
                  ["--image-source" as any]: `url(/oss_promo_presentations_${
                    theme === THEME.DARK ? "dark" : "light"
                  }.svg)`,
                  backgroundSize: "60%",
                  opacity: 0.4,
                }}
              />
              <div className="app-sidebar-promo-text">
                Create presentations with Excalidraw+
              </div>
              <LinkButton
                href={`${
                  import.meta.env.VITE_APP_PLUS_LP
                }/plus?utm_source=excalidraw&utm_medium=app&utm_content=presentations_promo#excalidraw-redirect`}
              >
                Sign up now
              </LinkButton>
            </div>
          </Sidebar.Tab>
          <Sidebar.Tab tab="animation">
            <AnimationPanel />
          </Sidebar.Tab>
          <Sidebar.Tab tab="settings">
            <SettingsPanel />
          </Sidebar.Tab>
        </DefaultSidebar>
      </ActiveClipProvider>
    </FlipbookProvider>
  );
};
